import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Filter, X, ArrowUpDown, ArrowUp, ArrowDown, Database } from "lucide-react";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MetaDataTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [columns, setColumns] = useState([]);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 25;
  
  // Sorting state
  const [sortField, setSortField] = useState("entry_time");
  const [sortOrder, setSortOrder] = useState("desc");
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    mtf_status: ""
  });
  
  // Export loading state
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sort_field: sortField,
        sort_order: sortOrder
      });
      
      // Add mtf_status filter if set
      if (filters.mtf_status) params.append("mtf_status", filters.mtf_status);
      
      const response = await axios.get(`${API}/meta-model-data?${params.toString()}`);
      
      setData(response.data.data);
      setTotalPages(response.data.total_pages);
      setTotalCount(response.data.total_count);
      
      // Extract columns from first row if data exists
      if (response.data.data.length > 0) {
        setColumns(Object.keys(response.data.data[0]));
      }
    } catch (err) {
      console.error("Error fetching meta data:", err);
      setError(err.response?.data?.detail || "Failed to fetch meta data");
      toast.error("Failed to fetch meta data");
    } finally {
      setLoading(false);
    }
  }, [page, sortField, sortOrder, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
    setPage(1); // Reset to first page on sort change
  };

  const handleFilterChange = (key, value) => {
    // Handle "all" value as empty string for filtering
    const filterValue = value === "all" ? "" : value;
    setFilters(prev => ({ ...prev, [key]: filterValue }));
    setPage(1); // Reset to first page on filter change
  };

  const clearFilters = () => {
    setFilters({
      mtf_status: ""
    });
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.mtf_status) params.append("mtf_status", filters.mtf_status);
      
      const response = await axios.get(`${API}/meta-model-data/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `meta_model_data_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export completed successfully");
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  const formatCellValue = (value, column) => {
    if (value === null || value === undefined) return "-";
    
    // Format dates
    if (column.includes("time") || column.includes("_at")) {
      try {
        return new Date(value).toLocaleString();
      } catch {
        return value;
      }
    }
    
    // Format numbers with decimals
    if (typeof value === "number") {
      if (column.includes("percent") || column.includes("pct")) {
        return `${value.toFixed(2)}%`;
      }
      if (column.includes("price") || column.includes("pnl") || column.includes("size")) {
        return `$${value.toFixed(2)}`;
      }
      if (Number.isInteger(value)) {
        return value.toString();
      }
      return value.toFixed(4);
    }
    
    // Format booleans
    if (typeof value === "boolean") {
      return value ? "✓" : "✗";
    }
    
    return String(value);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    }
    return sortOrder === "asc" 
      ? <ArrowUp className="w-3 h-3 ml-1 text-primary" />
      : <ArrowDown className="w-3 h-3 ml-1 text-primary" />;
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== "");

  return (
    <div className="space-y-4" data-testid="meta-data-table">
      {/* Header with title and actions */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">Meta Model Data</h2>
          <span className="text-sm text-muted-foreground">({totalCount} records)</span>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={hasActiveFilters ? "border-primary" : ""}
            data-testid="filter-toggle-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters {hasActiveFilters && `(${Object.values(filters).filter(v => v).length})`}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting || totalCount === 0}
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="glass-card p-4 rounded-lg border border-white/10 space-y-4" data-testid="filter-panel">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">Filters</span>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Clear All
              </Button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* MTF Status Filter */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">MTF Status</label>
              <Select 
                value={filters.mtf_status || "all"} 
                onValueChange={(v) => handleFilterChange("mtf_status", v)}
              >
                <SelectTrigger data-testid="filter-mtf-status">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="EXECUTE">EXECUTE</SelectItem>
                  <SelectItem value="WAIT">WAIT</SelectItem>
                  <SelectItem value="SKIP">SKIP</SelectItem>
                  <SelectItem value="HOLD">HOLD</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-3 text-muted-foreground">Loading data...</span>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="glass-card p-8 text-center border border-red-500/30">
          <p className="text-red-400 mb-4">{error}</p>
          <Button onClick={fetchData} variant="outline">
            Retry
          </Button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && data.length === 0 && (
        <div className="glass-card p-8 text-center">
          <Database className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No meta model data found</p>
          {hasActiveFilters && (
            <Button onClick={clearFilters} variant="outline" className="mt-4">
              Clear Filters
            </Button>
          )}
        </div>
      )}

      {/* Data Table */}
      {!loading && !error && data.length > 0 && (
        <>
          <div className="glass-card rounded-lg border border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="meta-data-grid">
                <thead className="bg-black/40 border-b border-white/10">
                  <tr>
                    {columns.map((col) => (
                      <th
                        key={col}
                        className="px-3 py-2 text-left font-medium text-muted-foreground whitespace-nowrap cursor-pointer hover:bg-white/5 transition-colors"
                        onClick={() => handleSort(col)}
                      >
                        <div className="flex items-center">
                          {col.replace(/_/g, " ")}
                          {getSortIcon(col)}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {data.map((row, rowIndex) => (
                    <tr key={rowIndex} className="hover:bg-white/5 transition-colors">
                      {columns.map((col) => (
                        <td key={col} className="px-3 py-2 whitespace-nowrap">
                          {formatCellValue(row[col], col)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4" data-testid="pagination">
            <div className="text-sm text-muted-foreground">
              Showing {((page - 1) * limit) + 1} - {Math.min(page * limit, totalCount)} of {totalCount} records
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(1)}
                disabled={page === 1}
                data-testid="pagination-first"
              >
                <ChevronsLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                data-testid="pagination-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <span className="px-4 py-1 text-sm">
                Page {page} of {totalPages}
              </span>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                data-testid="pagination-next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                data-testid="pagination-last"
              >
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MetaDataTable;
